/**
 * React Query hooks for referral data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import { CACHE_TIMES } from '../utils/constants';
import type { ReferralStats, ReferralCodeResponse, QualificationRequirements } from '../types';

/**
 * Query keys for referral data
 */
export const referralKeys = {
  all: ['referrals'] as const,
  stats: () => [...referralKeys.all, 'stats'] as const,
  code: () => [...referralKeys.all, 'code'] as const,
  status: () => [...referralKeys.all, 'status'] as const,
  requirements: () => [...referralKeys.all, 'requirements'] as const,
};

/**
 * Fetch referral statistics
 */
export function useReferralStats() {
  return useQuery({
    queryKey: referralKeys.stats(),
    queryFn: async (): Promise<ReferralStats> => {
      const data = await api.getReferralStats();
      return {
        referralCode: data.referralCode ?? null,
        totalReferrals: data.totalReferrals ?? 0,
        qualifiedReferrals: data.qualifiedReferrals ?? 0,
        pendingReferrals: data.pendingReferrals ?? 0,
        rewardsThisMonth: data.rewardsThisMonth ?? 0,
        referrals: data.referrals ?? [],
        premiumDaysRemaining: data.premiumDaysRemaining,
        premiumExpiresAt: data.premiumExpiresAt,
      };
    },
    staleTime: CACHE_TIMES.STATS,
    gcTime: CACHE_TIMES.STATS * 2,
  });
}

/**
 * Fetch or generate referral code
 */
export function useReferralCode(enabled = true) {
  return useQuery({
    queryKey: referralKeys.code(),
    queryFn: async (): Promise<ReferralCodeResponse> => {
      const data = await api.getReferralCode();
      return { referralCode: data.referralCode };
    },
    enabled,
    staleTime: CACHE_TIMES.CODE,
    gcTime: CACHE_TIMES.CODE * 2,
  });
}

/**
 * Fetch qualification requirements
 */
export function useQualificationRequirements() {
  return useQuery({
    queryKey: referralKeys.requirements(),
    queryFn: async (): Promise<QualificationRequirements> => {
      const data = await api.getReferralRequirements();
      return {
        method: data.method ?? 'as_is_scans',
        storeHasAsIs: data.storeHasAsIs ?? true,
        asIsScans: {
          required: data.asIsScans?.required ?? 3,
          current: data.asIsScans?.current ?? 0,
          isComplete: data.asIsScans?.isComplete ?? false,
        },
        levelBased: {
          requiredXp: data.levelBased?.requiredXp ?? 1500,
          requiredLevel: data.levelBased?.requiredLevel ?? 2,
          currentXp: data.levelBased?.currentXp ?? 0,
          currentLevel: data.levelBased?.currentLevel ?? 1,
          isComplete: data.levelBased?.isComplete ?? false,
        },
        subscription: {
          description: data.subscription?.description ?? 'Subscribe to Pro or Pro+ for instant qualification',
        },
      };
    },
    staleTime: CACHE_TIMES.REQUIREMENTS,
    gcTime: CACHE_TIMES.REQUIREMENTS * 2,
  });
}

/**
 * Apply a referral code (for referred users)
 */
export function useApplyReferralCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      return api.applyReferralCode(code);
    },
    onSuccess: () => {
      // Invalidate relevant queries after applying code
      queryClient.invalidateQueries({ queryKey: referralKeys.status() });
      queryClient.invalidateQueries({ queryKey: referralKeys.requirements() });
    },
  });
}

/**
 * Combined hook for all referral data
 */
export function useReferralData() {
  const statsQuery = useReferralStats();
  const codeQuery = useReferralCode(!statsQuery.data?.referralCode);
  const requirementsQuery = useQualificationRequirements();

  // Derive referral code from stats or dedicated code query
  const referralCode = statsQuery.data?.referralCode || codeQuery.data?.referralCode || '';

  return {
    // Data
    stats: statsQuery.data,
    referralCode,
    requirements: requirementsQuery.data,

    // Loading states
    isLoading: statsQuery.isLoading,
    isLoadingCode: codeQuery.isLoading,
    isLoadingRequirements: requirementsQuery.isLoading,

    // Error states
    error: statsQuery.error,
    codeError: codeQuery.error,
    requirementsError: requirementsQuery.error,

    // Refetch functions
    refetchStats: statsQuery.refetch,
    refetchCode: codeQuery.refetch,
    refetchRequirements: requirementsQuery.refetch,
  };
}
